import { Link } from "react-router-dom";
import { HERO_IMAGE } from "@/data/animals";
import { ArrowRight } from "@phosphor-icons/react";
import { Editable } from "@/components/Editable";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="font-mono-label text-[11px] text-[#5C6B5E]">
            § About El Porvenir
          </div>
          <h1 className="font-serif-display text-6xl md:text-7xl text-[#2A3A2C] mt-3 leading-[0.95]">
            <Editable id="about.heading" multiline defaultValue={"A small village.\nA large wilderness."} />
          </h1>
          <div className="ink-rule mt-8" />
          <div className="mt-8 space-y-6 text-lg leading-relaxed text-[#2E362F]">
            <p>
              <Editable
                id="about.p1"
                multiline
                defaultValue={"El Porvenir sits at about 7,290 feet in the Sangre de Cristo foothills of northern New Mexico, tucked into San Miguel County west of Las Vegas. The name means \"the future\" in Spanish — a quietly hopeful name for a quiet place."}
              />
            </p>
            <p>
              <Editable
                id="about.p2"
                multiline
                defaultValue={"The Gallinas River, born from a cold spring above the canyon, shapes everything here. It feeds the trout, draws the bears down in late summer for the chokecherries, and gives the hummingbirds their wildflower-lined meadows. Up the canyon, the forest is dense enough that even the locals admit they don't always know what's moving through it."}
              />
            </p>
            <p>
              <Editable
                id="about.p3"
                multiline
                defaultValue={"This guide is a small offering: nine creatures (and one unverified resident), each with their own particular way of belonging to this place."}
              />
            </p>
          </div>

          <Link
            to="/"
            data-testid="about-cta"
            className="mt-10 inline-flex items-center gap-2 bg-[#2A3A2C] text-[#F4F1EA] px-7 py-4 font-mono-label text-[11px] hover:bg-[#1A251C] transition-colors"
          >
            Open the Field Guide <ArrowRight size={16} />
          </Link>
        </div>

        <div className="md:col-span-5">
          <div className="aspect-[4/5] overflow-hidden border border-[#D1C9B8]">
            <img
              src={HERO_IMAGE}
              alt="El Porvenir landscape"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="font-mono-label text-[10px] text-[#5C6B5E] mt-3 flex justify-between">
            <span>Plate · Frontispiece</span>
            <span>Sangre de Cristos</span>
          </div>
        </div>
      </section>

      {/* Principles strip */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-24 md:mt-32">
        <div className="font-mono-label text-[11px] text-[#5C6B5E]">
          § Field Ethics
        </div>
        <h2 className="font-serif-display text-4xl md:text-5xl text-[#2A3A2C] mt-3 leading-tight">
          <Editable id="about.ethics.heading" defaultValue="How to watch without disturbing." />
        </h2>
        <div className="ink-rule mt-8" />
        <div className="mt-10 grid md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Distance",
              b: "Use binoculars instead of stepping closer. If an animal changes its behavior because of you, you are too close." },
            { n: "02", t: "Food & Trash",
              b: "Store everything bear-proof. A habituated bear is a dead bear. Pack out what you pack in — every wrapper, every peel." },
            { n: "03", t: "Sound & Stillness",
              b: "Move quietly. Speak softly. The forest speaks back only when you stop talking over it." },
          ].map((p) => (
            <div key={p.n} className="border-l border-[#D1C9B8] pl-5">
              <div className="font-mono-label text-[10px] text-[#B75D3F]">No. {p.n}</div>
              <div className="font-serif-display text-3xl text-[#2A3A2C] mt-1">
                <Editable id={`about.ethics.${p.n}.title`} defaultValue={p.t} />
              </div>
              <p className="text-[#5C6B5E] mt-3 leading-relaxed">
                <Editable id={`about.ethics.${p.n}.body`} multiline defaultValue={p.b} />
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="pb-24" />
    </div>
  );
}
